import React, { useEffect, useState } from 'react';
import { TRANSITION_TYPE } from '../../constants/editorConst';

const initialDimensions = { width: 100, height: 100 };

const TransitionSimulation = ({
  isVideoPlaying,
  transitions,
  playedMs,
  currentVideoElement,
  parentNode,
  hideOnVideoPaused = false,
  videoMaxDimensions,
  isIOS,
  isFullScreen,
  isMobileView,
}) => {
  const [opacity, setOpacity] = useState(0);
  const [dimensions, setDimensions] = useState(initialDimensions);
  const [currentType, setCurrentType] = useState(TRANSITION_TYPE.DIP_TO_BLACK);
  const [currentTransition, setCurrentTransition] = useState(null);

  useEffect(() => {
    if ((hideOnVideoPaused ? isVideoPlaying : true) && transitions) {
      let transition = null;

      if (currentVideoElement) {
        transition = transitions.find(
          item => currentVideoElement?.sceneId === item.leftScene?.id || currentVideoElement?.sceneId === item.rightScene?.id
        );
      } else {
        transition = transitions.find(item => playedMs >= item.start && playedMs <= item.end);
      }

      if (transition) {
        const transitionIndex = transitions.indexOf(transition);
        setCurrentTransition(transitionIndex);
      } else {
        setCurrentTransition(null);
      }
    } else {
      setCurrentTransition(null);
    }
  }, [playedMs, transitions]);

  useEffect(() => {
    let newOpacity = 0;
    if (transitions && currentTransition !== null) {
      if (transitions[currentTransition]) {
        const { start, end, durationMs, type, customTransitionRatio, leftScene, rightScene } = transitions[currentTransition];
        setCurrentType(type);

        // In multiplyMode (clip organizer), when changing the scene,
        // the playedMs value is sometimes wrong, so in this mode we should
        // take the time value from the current player
        if (currentVideoElement) {
          const currentTime = currentVideoElement?.video?.currentTime ?? 0;

          const isLeftScene = leftScene?.id === currentVideoElement?.sceneId;
          const isLeftSceneActive = leftScene && leftScene?.trimEnd - currentTime * 1000 <= durationMs / 2;

          const isRightScene = rightScene?.id === currentVideoElement?.sceneId;
          const isRightSceneActive = rightScene && currentTime * 1000 - rightScene.trimStart <= durationMs / 2;

          if (isLeftScene && isLeftSceneActive) {
            newOpacity = ((durationMs / 2 - (leftScene.trimEnd - currentTime * 1000)) * 0.2) / 100;
          }

          if (isRightScene && isRightSceneActive) {
            newOpacity = ((durationMs / 2 - (currentTime * 1000 - rightScene.trimStart)) * 0.2) / 100;
          }
        } else {
          if (playedMs - start <= durationMs / 2) {
            newOpacity = ((playedMs - start) * 0.2) / 100;
          } else {
            newOpacity = Math.max(0, ((end - playedMs) * 0.2) / 100);
          }
        }

        if (customTransitionRatio) {
          let playerRatio = 0.5625; // 9 : 16

          if (parentNode) {
            playerRatio = parentNode.offsetHeight / parentNode.offsetWidth;
          }
          setDimensions({
            width: (playerRatio / customTransitionRatio) * 100,
            height: 100,
          });
        } else {
          // As fullscreen mode for iOS is simulated by making player full screen size, 
          // simulation of the transition should be done considering video content dimensions (by videoMaxDimensions)
          if (isIOS && isFullScreen) {
            let height = initialDimensions.height;
            let width = initialDimensions.width;

            const screenResolution = window.innerWidth / window.innerHeight;
            // Phone in landscape mode
            if (screenResolution > 1) {
              const widthWithCoeff = initialDimensions.height * videoMaxDimensions.width;
              width = widthWithCoeff / screenResolution;
            } else {
              // Phone in portrait mode
              const heightWithCoeff = initialDimensions.width / videoMaxDimensions.width;
              height = heightWithCoeff * screenResolution;
            }
            setDimensions({ width, height });
          } else {
            setDimensions(initialDimensions);
          }
        }
      }
    }
    setOpacity(newOpacity);
  }, [playedMs]);

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: `${Math.min(Math.ceil(dimensions.width), 100)}%`,
        height: `${dimensions.height}%`,
        transform: 'translate(-50%, -50%)',
        opacity,
        backgroundColor: currentType !== null ? (currentType === TRANSITION_TYPE.DIP_TO_BLACK ? '#000000' : '#FFFFFF') : 'transparent',
        zIndex: 51,
        pointerEvents: 'none',
        borderRadius: dimensions.width >= 95 && !isMobileView ? '8px' : '0',
      }}
    ></div>
  );
};

export default TransitionSimulation;
